// Freezes say what froze (owner, 2026-10-24: the billing board hung for 28 s after
// a sign-in and the device log was silent — the page was too busy to write).
//
// Two witnesses, both written to the interaction log and flushed at once:
// - a heartbeat every second: when it fires late, the page was stuck for that
//   long ("main_thread_stall", with the route) — works in every browser;
// - Chrome's long-task reports for any task over a second ("long_task", with the
//   script that ran it when Chrome says).
// A hidden tab's timers are throttled by the browser; that is not a stall.

const HEARTBEAT_MS = 1000;
const STALL_MS = 3000;
const LONG_TASK_MS = 1000;
// Longer than this is a sleeping laptop or a suspended tab, not a frozen page.
const SLEEP_MS = 5 * 60_000;

export function startStallWatch({
  now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()),
  log,
  flush,
  route = () => window.location.pathname,
  visible = () => document.visibilityState !== "hidden",
  observeLongTasks = true,
} = {}) {
  let last = now();
  // A hidden tab's timers are throttled: the first tick after it returns is late
  // by however long it was hidden. Start counting again when it shows.
  const onVisibility = () => { if (visible()) last = now(); };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
  const timer = setInterval(() => {
    const at = now();
    const late = Math.round(at - last - HEARTBEAT_MS);
    last = at;
    if (late >= STALL_MS && late < SLEEP_MS && visible()) {
      log("main_thread_stall", { stalled_ms: late, route: route() });
      try { flush(); } catch { /* logging never throws into the app */ }
    }
  }, HEARTBEAT_MS);

  let observer = null;
  if (observeLongTasks && typeof PerformanceObserver === "function") {
    try {
      observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.duration < LONG_TASK_MS) continue;
          const source = entry.attribution?.[0];
          log("long_task", {
            duration_ms: Math.round(entry.duration),
            route: route(),
            source: source ? [source.containerType, source.containerSrc || source.containerName].filter(Boolean).join(" ") : null,
          });
        }
        try { flush(); } catch { /* logging never throws into the app */ }
      });
      observer.observe({ type: "longtask", buffered: true });
    } catch {
      observer = null; // not supported here; the heartbeat still covers it
    }
  }

  return {
    stop() {
      clearInterval(timer);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
      if (observer) observer.disconnect();
    },
  };
}
